import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";

/**
 * Deleting a visual has to clean up storage as well as rows, and every call
 * site was getting only half of that right.
 *
 * `deliverables.visual_id` and `visuals.root_render_id` are both ON DELETE
 * CASCADE, so removing a master silently takes its format variants and its
 * edit versions with it. The rows go, but nothing ever removed the *files*
 * those rows pointed at, so every delete left the renders bucket holding
 * assets nothing references any more.
 *
 * Three sources of files hang off a visual:
 *   - `visuals.storage_path`      — the image itself
 *   - `deliverables.asset_path`   — each generated format variant
 *   - `videos[].path` on both     — generated video versions
 *
 * This collects all of them, for the given visuals *and* the edit versions
 * that will cascade, then removes the files before deleting the rows.
 */

const BUCKET = "renders";

/** PostgREST puts `.in()` lists in the URL, so keep each request bounded. */
const CHUNK = 100;

function chunked<T>(items: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK));
  return out;
}

/** `videos` is a jsonb array of `{ path, ... }` entries in the same bucket. */
function videoPaths(videos: unknown): string[] {
  if (!Array.isArray(videos)) return [];
  return videos
    .map((v) => (v && typeof v === "object" ? (v as any).path : null))
    .filter((p): p is string => typeof p === "string" && p.length > 0);
}

/**
 * The given visuals plus the edit versions that cascade with them via
 * root_render_id. Anything counting or removing assets has to work from this
 * set, not the caller's ids, or it will under-report what a delete destroys.
 */
async function expandWithDescendants(visualIds: string[]): Promise<string[]> {
  const descendants: string[] = [];
  for (const batch of chunked(visualIds)) {
    const { data } = await supabase.from("visuals").select("id").in("root_render_id", batch);
    for (const row of data || []) {
      if (!visualIds.includes((row as any).id)) descendants.push((row as any).id);
    }
  }
  return [...new Set([...visualIds, ...descendants])];
}

/**
 * Every storage path owned by these visuals, including the edit versions that
 * cascade with them. Exported so callers can report what a delete will remove.
 */
export async function collectVisualAssetPaths(visualIds: string[]): Promise<string[]> {
  if (visualIds.length === 0) return [];

  const allIds = await expandWithDescendants(visualIds);

  const paths: string[] = [];
  for (const batch of chunked(allIds)) {
    const [{ data: visuals }, { data: deliverables }] = await Promise.all([
      supabase.from("visuals").select("storage_path, videos").in("id", batch),
      supabase.from("deliverables").select("asset_path, videos").in("visual_id", batch),
    ]);
    for (const v of (visuals || []) as any[]) {
      if (v.storage_path) paths.push(v.storage_path);
      paths.push(...videoPaths(v.videos));
    }
    for (const d of (deliverables || []) as any[]) {
      if (d.asset_path) paths.push(d.asset_path);
      paths.push(...videoPaths(d.videos));
    }
  }
  return [...new Set(paths)];
}

/**
 * Remove the given visuals along with their format variants, edit versions,
 * videos, and all the files behind them.
 *
 * Throws if a row delete fails — an unchecked delete that errors looks exactly
 * like one that did nothing, which is how the earlier foreign-key rejections
 * went unnoticed. A *storage* failure only reports: an orphaned file is
 * recoverable, a half-deleted record is not.
 */
export async function deleteVisualsAndAssets(visualIds: string[]): Promise<void> {
  const ids = [...new Set(visualIds.filter(Boolean))];
  if (ids.length === 0) return;

  const paths = await collectVisualAssetPaths(ids);
  for (const batch of chunked(paths)) {
    const { error } = await supabase.storage.from(BUCKET).remove(batch);
    if (error) {
      console.error("Failed to remove visual assets from storage:", error);
      Sentry.captureException(error);
    }
  }

  for (const batch of chunked(ids)) {
    // deliverables cascade anyway; deleting them explicitly means the outcome
    // is checked rather than assumed.
    const { error: delErr } = await supabase.from("deliverables").delete().in("visual_id", batch);
    if (delErr) throw delErr;
    const { error: visErr } = await supabase.from("visuals").delete().in("id", batch);
    if (visErr) throw visErr;
  }
}

/**
 * How many format variants these visuals carry, so a confirm prompt can say
 * what is about to disappear instead of deleting them silently.
 */
export async function countDeliverables(visualIds: string[]): Promise<number> {
  const given = visualIds.filter(Boolean);
  if (given.length === 0) return 0;
  // Count across the cascade, so the number in the prompt is the number that
  // actually disappears.
  const ids = await expandWithDescendants(given);
  let total = 0;
  for (const batch of chunked(ids)) {
    const { count } = await supabase
      .from("deliverables")
      .select("id", { count: "exact", head: true })
      .in("visual_id", batch);
    total += count || 0;
  }
  return total;
}
